// client/src/components/pages/LexiStar/DispatchBoard/OrderCard.tsx (ST-00250)
//
// One order bubble: the order's reference, customer, material and lane, the absorption ring, the
// trucks on it (one chip per truck, "×2" for two loads, the cross releases the latest load), and
// the drop zone. A truck dragged from the rail (or a chip from another card) drops here; a
// selected truck (touch / keyboard) gets an "Allocate here" button. Colours never stand alone: the
// state has a word and a glyph on the ring, and the card's border echoes it.

import { useState, type DragEvent, type KeyboardEvent } from 'react';
import { FaPen, FaTimes, FaArrowRight, FaLock } from 'react-icons/fa';
import type { BoardAllocation, BoardOrder, BoardTruck } from '../../../../types/lexistar-dispatch-board';
import AbsorptionRing from './AbsorptionRing';
import { DRAG_TYPE, STATE_META, absorptionText, allocationsByTruck, decodeDrag, encodeDrag, fmtTonnes, fmtTime, remainingText, truckName } from './boardLogic';

export interface OrderCardProps {
  order: BoardOrder;
  canManage: boolean;
  busy: boolean;
  /** A truck selected on the rail (touch / keyboard path): offer "Allocate here". */
  selectedTruck: BoardTruck | null;
  onDropTruck: (order: BoardOrder, assetId: string) => void;
  onDropAllocation: (order: BoardOrder, fromOrderId: string, allocationId: string) => void;
  onRelease: (order: BoardOrder, allocation: BoardAllocation) => void;
  onEdit: (order: BoardOrder) => void;
}

const basisWord: Record<BoardAllocation['tonnes_basis'], string> = {
  truck: 'truck average',
  lane: 'lane average',
  order: 'order figure',
  none: 'no estimate',
};

function hasBoardDrag(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types || []).includes(DRAG_TYPE);
}

export default function OrderCard({ order, canManage, busy, selectedTruck, onDropTruck, onDropAllocation, onRelease, onEdit }: OrderCardProps) {
  const [over, setOver] = useState(false);
  const meta = STATE_META[order.absorption.state];
  const open = order.status === 'open';
  const droppable = canManage && open && !busy;
  const groups = allocationsByTruck(order);

  const onDragOver = (e: DragEvent<HTMLElement>) => {
    if (!droppable || !hasBoardDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!over) setOver(true);
  };
  const onDrop = (e: DragEvent<HTMLElement>) => {
    setOver(false);
    if (!droppable) return;
    const payload = decodeDrag(e.dataTransfer.getData(DRAG_TYPE));
    if (!payload) return;
    e.preventDefault();
    if (payload.kind === 'truck') onDropTruck(order, payload.asset_id);
    else if (payload.order_id !== order.id) onDropAllocation(order, payload.order_id, payload.allocation_id);
  };

  const chipDragStart = (e: DragEvent<HTMLElement>, allocation: BoardAllocation) => {
    e.dataTransfer.setData(DRAG_TYPE, encodeDrag({ kind: 'allocation', allocation_id: allocation.id, order_id: order.id, asset_id: allocation.asset_id }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const releaseKey = (e: KeyboardEvent<HTMLButtonElement>, allocation: BoardAllocation) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      onRelease(order, allocation);
    }
  };

  const lane = order.from_name && order.to_name ? `${order.from_name} → ${order.to_name}` : order.lane_name || 'Lane';
  const remaining = remainingText(order.absorption);

  return (
    <article
      data-testid={`order-card-${order.id}`}
      data-order-id={order.id}
      aria-label={`Order ${order.order_ref}${order.customer ? ` for ${order.customer}` : ''}: ${meta.word}`}
      aria-busy={busy || undefined}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={`relative flex flex-col gap-3 rounded-2xl border-2 bg-surface p-4 shadow-sm transition-colors ${over ? 'border-primary-500 ring-4 ring-primary-500/20' : meta.panel} ${!open ? 'opacity-80' : ''}`}
    >
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-bold text-slate-900 truncate" title={order.order_ref}>
              {order.order_ref}
            </h3>
            {order.shift && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-600">{order.shift}</span>
            )}
            {!open && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-700">
                <FaLock aria-hidden="true" size={9} /> {order.status}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-700 line-clamp-2">
            {[order.customer, order.material].filter(Boolean).join(' · ') || <span className="text-slate-400">No customer / material</span>}
          </p>
          <p className="text-xs text-slate-500 line-clamp-2" title={lane}>
            {lane}
            {order.route_code ? ` · ${order.route_code}` : ''} · from {order.start_time}
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => onEdit(order)}
            aria-label={`Edit order ${order.order_ref}`}
            className="touch-target p-2 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100"
          >
            <FaPen size={12} />
          </button>
        )}
      </header>

      <div className="flex items-center gap-4">
        <AbsorptionRing absorption={order.absorption} size={88} />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold text-slate-900 tabular-nums">{absorptionText(order.absorption)}</p>
          {remaining && <p className={`text-xs font-medium ${meta.text}`}>{remaining}</p>}
          {order.absorption.target_kind === 'tonnes' && order.tonnes_per_load != null && (
            <p className="text-xs text-slate-500">Planning {fmtTonnes(order.tonnes_per_load)} per load</p>
          )}
          {order.absorption.target_kind === 'tonnes' && order.target_loads != null && (
            <p className="text-xs text-slate-500">Also {order.target_loads} loads asked</p>
          )}
        </div>
      </div>

      <ul className="flex flex-wrap gap-1.5 min-h-[2.25rem]" aria-label={`Trucks on ${order.order_ref}`}>
        {groups.length === 0 && (
          <li className="text-xs text-slate-400 self-center">{droppable ? 'Drop a truck here' : 'No trucks'}</li>
        )}
        {groups.map((g) => {
          const latest = g.allocations[g.allocations.length - 1] as BoardAllocation;
          const tonnes = g.allocations.reduce((s, a) => s + (a.planned_tonnes ?? 0), 0);
          const title = `${g.name}: ${g.allocations.length} load${g.allocations.length === 1 ? '' : 's'}${tonnes ? `, ${fmtTonnes(tonnes)}` : ''} (${basisWord[latest.tonnes_basis]}) · first load ${fmtTime(g.allocations[0]?.planned_start)}${latest.trip_reference ? ` · ${latest.trip_reference}` : ''}`;
          return (
            <li
              key={g.asset_id}
              draggable={canManage && latest.trip_stage === 'PLANNED'}
              onDragStart={(e) => chipDragStart(e, latest)}
              title={title}
              data-testid={`chip-${order.id}-${g.asset_id}`}
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 pl-2.5 pr-1 py-0.5 text-xs font-medium text-slate-800"
            >
              <span className="truncate max-w-[9rem]">{g.name}</span>
              {g.allocations.length > 1 && <span className="font-bold tabular-nums">×{g.allocations.length}</span>}
              {tonnes > 0 && <span className="text-slate-500 tabular-nums">{fmtTonnes(tonnes)}</span>}
              {latest.trip_stage && latest.trip_stage !== 'PLANNED' && (
                <span className="px-1 rounded bg-blue-700 text-blue-50 text-[10px] uppercase">{latest.trip_stage.replace(/_/g, ' ').toLowerCase()}</span>
              )}
              {canManage && (
                <button
                  type="button"
                  disabled={busy || latest.trip_stage !== 'PLANNED'}
                  onClick={() => onRelease(order, latest)}
                  onKeyDown={(e) => releaseKey(e, latest)}
                  aria-label={`Release ${g.name} from ${order.order_ref}${g.allocations.length > 1 ? ` (1 of ${g.allocations.length} loads)` : ''}`}
                  title={latest.trip_stage !== 'PLANNED' ? 'Already dispatched: cancel the trip itself' : 'Release this load (its planned trip is cancelled)'}
                  className="touch-target-sm ml-0.5 rounded-full p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-900 disabled:opacity-40"
                >
                  <FaTimes size={10} />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {selectedTruck && canManage && open && (
        <button
          type="button"
          disabled={busy || selectedTruck.availability !== 'available'}
          onClick={() => onDropTruck(order, selectedTruck.asset_id)}
          className="inline-flex items-center justify-center gap-2 w-full rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 disabled:bg-slate-300"
        >
          <FaArrowRight aria-hidden="true" size={11} /> Allocate {truckName(selectedTruck)} here
        </button>
      )}
      {order.notes && <p className="text-xs text-slate-500 italic truncate" title={order.notes}>{order.notes}</p>}
    </article>
  );
}
